import React from 'react'
import { Box, Collapse, IconButton, Typography } from '@mui/material'
import { alpha, Theme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

export type TierVariant = 'company' | 'bayesprice' | 'mine' | 'colleagues'

// Scope markers (1 Sep call): all three tiers carry a tint plus a distinct,
// vibrant left-edge marker in a standard system colour — company = primary,
// bayesprice = success, mine = info, colleagues = secondary — so scope reads at a glance and the
// markers hold against the tints rather than a white ground.
const TIER_VARIANT_SX: Record<TierVariant, object> = {
  company: {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
    borderLeft: (theme: Theme) => `4px solid ${theme.palette.primary.main}`,
    p: 1,
  },
  bayesprice: {
    backgroundColor: (theme: Theme) => alpha(theme.palette.success.main, 0.05),
    borderLeft: (theme: Theme) => `4px solid ${theme.palette.success.main}`,
    p: 1,
  },
  mine: {
    backgroundColor: (theme: Theme) => alpha(theme.palette.info.main, 0.05),
    borderLeft: (theme: Theme) => `4px solid ${theme.palette.info.main}`,
    p: 1,
  },
  colleagues: {
    backgroundColor: (theme: Theme) => alpha(theme.palette.secondary.main, 0.05),
    borderLeft: (theme: Theme) => `4px solid ${theme.palette.secondary.main}`,
    p: 1,
  },
}

interface TierSectionProps {
  title: string
  count: number
  open: boolean
  onToggle: () => void
  headerExtra?: React.ReactNode
  children: React.ReactNode
  /** The Data Viz tint and edge marker for this tier. */
  variant?: TierVariant
  sx?: object
}

/** One collapsible tier section (#1179): disclosure header with count, and a
 *  light per-tier background tint so the three tiers read apart. Data Viz
 *  owns the look; other libraries (ThemeAI code frames, #1492) use this same
 *  component so they match it exactly. */
const TierSection: React.FC<TierSectionProps> = ({ title, count, open, onToggle, headerExtra, children, variant, sx }) => (
  <Box sx={{ borderRadius: 1, ...(variant ? TIER_VARIANT_SX[variant] : {}), ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 1 : 0 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        onClick={onToggle}
        role="button"
        aria-expanded={open}
        aria-label={`${title} section`}
      >
        <IconButton size="small" aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}{' '}
          <Typography component="span" variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            ({count})
          </Typography>
        </Typography>
      </Box>
      {headerExtra}
    </Box>
    <Collapse in={open}>{children}</Collapse>
  </Box>
)

export default TierSection
